import React from 'react';
import { Image } from 'antd';
import AppModal from '@/pages/inside/tenant/AppModal';
import StatusBadge from '@/components/ui/StatusBadge';
import Markdown from '@/components/ui/Markdown';
import Link from '@/components/ui/Link';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ModalChromeHeading,
    ModalBlockLabel,
    MutedText,
    CardDescriptionInner,
    PanelSectionLabel,
} from '@/pages/inside/tenant/style';
import { getClientTicketDisplayId, getClientTicketMarkdown } from '@hooks/projects/useProjectClientTickets';
import { getIssueOrUpgradeDisplayId, getIssueOrUpgradeMarkdown } from '@hooks/projects/useProjectIssuesAndUpgrades';
import {
    FileOutlined,
    LinkOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    FilePptOutlined,
    FileTextOutlined,
    FileZipOutlined,
    VideoCameraOutlined,
    AudioOutlined,
} from '@ant-design/icons';
import {
    resolveAttachmentUrl,
    normalizePhaseLinkUrl,
    isLikelyImageAttachmentUrl,
    getAttachmentFileExtension,
} from '@hooks/projects/projectHelpers';
import { GlobalImagePreviewStyles } from '@/components/ui/GlobalImagePreviewStyles';

export default function ClientTicketDetailModal({ item, onClose, kind = 'client_ticket', displayColumnId = null }) {
    const open = Boolean(item);
    const [previewImage, setPreviewImage] = React.useState('');
    const forceDownloadFile = React.useCallback((url, fileName) => {
        if (!url) return;
        try {
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName || '';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (_err) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    }, []);
    const title = String(item?.title ?? '').trim() || (kind === 'issue_upgrade' ? 'Issue detail' : 'Client ticket detail');
    const markdown = kind === 'issue_upgrade' ? getIssueOrUpgradeMarkdown(item) : getClientTicketMarkdown(item);
    const displayId = kind === 'issue_upgrade' ? getIssueOrUpgradeDisplayId(item) : getClientTicketDisplayId(item);
    const state = String(item?.state ?? '').trim().toLowerCase();
    const reason = String(item?.reason ?? '').trim();
    const status = String(item?.status ?? '').trim().toLowerCase();
    const issueUpgradeType = String(item?.type ?? '').trim().toLowerCase();
    const isUpgrade = kind === 'issue_upgrade' && issueUpgradeType === 'upgrade';
    const displayCol = String(displayColumnId ?? '').trim().toLowerCase();
    const statusBadge = (() => {
        if (
            displayCol === 'active' &&
            (status === 'submitted' || status === 'submitted_to_client')
        ) {
            return { key: 'in_progress', label: 'In Progress' };
        }
        return { key: status, label: null };
    })();
    const attachmentItems = Array.isArray(item?.attachments)
        ? item.attachments
        : Array.isArray(item?.attachmentUrls)
          ? item.attachmentUrls
          : [];
    const links = attachmentItems.filter((x) => String(x?.type ?? '').toLowerCase() === 'link');
    const files = attachmentItems.filter((x) => String(x?.type ?? '').toLowerCase() !== 'link');
    const descriptionAttachmentItems = React.useMemo(
        () =>
            attachmentItems.map((x) => ({
                type: String(x?.type || '').toLowerCase() === 'link' ? 'link' : 'file',
                url: x?.url,
                title: x?.title ?? x?.url,
            })),
        [attachmentItems],
    );
    const fileIconForExtension = (ext) => {
        const e = String(ext || '').toLowerCase();
        if (e === 'pdf') return <FilePdfOutlined style={{ fontSize: 24 }} />;
        if (e === 'doc' || e === 'docx') return <FileWordOutlined style={{ fontSize: 24 }} />;
        if (e === 'xls' || e === 'xlsx' || e === 'csv') return <FileExcelOutlined style={{ fontSize: 24 }} />;
        if (e === 'ppt' || e === 'pptx') return <FilePptOutlined style={{ fontSize: 24 }} />;
        if (['zip', 'rar', '7z', 'tar', 'gz'].includes(e)) return <FileZipOutlined style={{ fontSize: 24 }} />;
        if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(e)) return <VideoCameraOutlined style={{ fontSize: 24 }} />;
        if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(e)) return <AudioOutlined style={{ fontSize: 24 }} />;
        if (['txt', 'md', 'json', 'xml'].includes(e)) return <FileTextOutlined style={{ fontSize: 24 }} />;
        return <FileOutlined style={{ fontSize: 24 }} />;
    };

    return (
        <AppModal
            size="lg"
            open={open}
            onCancel={onClose}
            title={
                item ? (
                    <Container $display="flex" $flexDirection="column" $gap="4px">
                        <MutedText style={{ fontSize: 12, margin: 0 }}>{displayId}</MutedText>
                        <ModalChromeHeading style={{ fontSize: 20 }}>{title}</ModalChromeHeading>
                    </Container>
                ) : null
            }
            footer={
                <CreateRoleModalFooter $inFooterSlot>
                    <Button $primary={false} $size="14px" onClick={onClose}>
                        Close
                    </Button>
                </CreateRoleModalFooter>
            }
        >
            <GlobalImagePreviewStyles />
            {open && item ? (
                <CreateRoleModalBody $flush>
                    <Container $display="flex" $flexDirection="column" $gap="16px">
                        {kind === 'issue_upgrade' ? (
                            <Container $display="flex" $alignItems="center" $gap="8px">
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '4px 10px',
                                        borderRadius: 999,
                                        fontSize: 12,
                                        fontWeight: 800,
                                        lineHeight: 1,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                        color: isUpgrade
                                            ? 'var(--ant-color-primary-text, #531dab)'
                                            : 'var(--ant-color-error-text, #a8071a)',
                                        background: isUpgrade
                                            ? 'var(--ant-color-primary-bg, rgba(124,58,237,0.10))'
                                            : 'var(--ant-color-error-bg, rgba(255,77,79,0.10))',
                                        border: isUpgrade
                                            ? '1px solid color-mix(in srgb, var(--ant-color-primary, #7c3aed) 28%, transparent)'
                                            : '1px solid color-mix(in srgb, var(--ant-color-error, #ff4d4f) 28%, transparent)',
                                    }}
                                >
                                    {isUpgrade ? 'UPGRADE' : 'ISSUE'}
                                </span>
                            </Container>
                        ) : (
                            <Container $display="flex" $alignItems="center" $gap="8px">
                                <StatusBadge status={statusBadge.key} size="small">
                                    {statusBadge.label || undefined}
                                </StatusBadge>
                                {state === 'genuine' || state === 'improper' ? (
                                    <StatusBadge status={state} size="small" />
                                ) : null}
                            </Container>
                        )}
                        <div>
                            <ModalBlockLabel as="div" style={{ display: 'block', marginBottom: 8 }}>
                                Description
                            </ModalBlockLabel>
                            {markdown ? (
                                <Markdown
                                    source={markdown}
                                    component={CardDescriptionInner}
                                    richInlineAnchors
                                    attachmentItems={descriptionAttachmentItems}
                                    linkPrimary
                                    onLinkClick={({ event, href, rawHref, isImage }) => {
                                        const isAttachmentRef = /^#attachment-(\d+)$/i.test(String(rawHref || ''));
                                        if (isImage && href) {
                                            event?.preventDefault?.();
                                            event?.stopPropagation?.();
                                            setPreviewImage(href);
                                            return;
                                        }
                                        if (isAttachmentRef && href) {
                                            event?.preventDefault?.();
                                            event?.stopPropagation?.();
                                            window.open(href, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                />
                            ) : (
                                <MutedText style={{ margin: 0 }}>No description.</MutedText>
                            )}
                        </div>
                        {reason ? (
                            <div>
                                <ModalBlockLabel as="div" style={{ display: 'block', marginBottom: 8 }}>
                                    Reason
                                </ModalBlockLabel>
                                <Markdown source={reason} component={CardDescriptionInner} />
                            </div>
                        ) : null}
                        {links.length > 0 ? (
                            <div>
                                <PanelSectionLabel
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        marginBottom: 8,
                                        fontSize: 11,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                        opacity: 0.75,
                                    }}
                                >
                                    <LinkOutlined />
                                    Links
                                </PanelSectionLabel>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {links.map((linkItem, idx) => {
                                        const href = normalizePhaseLinkUrl(linkItem?.url || '');
                                        const label = (linkItem?.title || href || 'Link').trim();
                                        return (
                                            <a
                                                key={`${linkItem?.url || label}-${idx}`}
                                                href={href || '#'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    maxWidth: '100%',
                                                    color: 'var(--ant-color-primary, #7c3aed)',
                                                    border: '1px solid rgba(124,58,237,0.22)',
                                                    background: 'var(--ant-color-primary-bg, rgba(124,58,237,0.08))',
                                                    borderRadius: 999,
                                                    padding: '6px 10px',
                                                    fontSize: 13,
                                                    overflowWrap: 'anywhere',
                                                    wordBreak: 'break-word',
                                                    textDecoration: 'none',
                                                }}
                                            >
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                    <LinkOutlined />
                                                    <span>{label}</span>
                                                </span>
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}
                        {files.length > 0 ? (
                            <div>
                                <PanelSectionLabel
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        marginBottom: 8,
                                        fontSize: 11,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                        opacity: 0.75,
                                    }}
                                >
                                    <FileOutlined />
                                    Attachments
                                </PanelSectionLabel>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 120px))', gap: 10 }}>
                                    {files.map((fileItem, idx) => {
                                        const rawUrl = String(fileItem?.url || '').trim();
                                        const href = resolveAttachmentUrl(rawUrl);
                                        const label = String(fileItem?.title || fileItem?.url || 'Attachment').trim();
                                        const isImage = isLikelyImageAttachmentUrl(rawUrl);
                                        const ext = getAttachmentFileExtension(rawUrl);
                                        return (
                                            <Link
                                                key={`${fileItem?.url || label}-${idx}`}
                                                href={href || '#'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => {
                                                    if (isImage && href) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setPreviewImage(href);
                                                    }
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 120,
                                                    height: 120,
                                                    gap: 6,
                                                    padding: 8,
                                                    borderRadius: 10,
                                                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                    background: 'var(--ant-color-fill-tertiary, rgba(0,0,0,0.06))',
                                                    textDecoration: 'none',
                                                    color: 'inherit',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {isImage ? (
                                                    <img
                                                        src={href}
                                                        alt={label}
                                                        style={{
                                                            width: '100%',
                                                            height: 82,
                                                            borderRadius: 8,
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            height: 82,
                                                            borderRadius: 8,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            background: 'var(--ant-color-fill-quaternary, rgba(255,255,255,0.75))',
                                                            color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.65))',
                                                        }}
                                                    >
                                                        {fileIconForExtension(ext)}
                                                    </div>
                                                )}
                                                <span
                                                    style={{
                                                        width: '100%',
                                                        fontSize: 11,
                                                        lineHeight: 1.3,
                                                        textAlign: 'center',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    title={label}
                                                >
                                                    {label}
                                                </span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}
                    </Container>
                </CreateRoleModalBody>
            ) : null}
            <Image
                style={{ display: 'none' }}
                src={previewImage || undefined}
                preview={{
                    visible: !!previewImage,
                    src: previewImage || undefined,
                    rootClassName: 'global-image-preview-root',
                    onVisibleChange: (visible) => {
                        if (!visible) setPreviewImage('');
                    },
                }}
            />
        </AppModal>
    );
}
